import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Shield, AlertCircle } from 'lucide-react';

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            </div>
            <Button onClick={() => navigate('/')} variant="ghost">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-slate-100 shadow-xl">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center">
              <FileText className="w-8 h-8 text-violet-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold">Terms of Use</h1>
              <p className="text-slate-600 mt-1">Last Updated: January 2026</p>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-bold text-yellow-900 mb-2">Important Notice</h3>
                <p className="text-yellow-800 text-sm">
                  <strong>DisputeKing.AI is NOT a credit repair company.</strong> We provide software tools, 
                  resources, and technology that empower individuals and credit repair professionals to manage 
                  their own credit repair processes. You maintain full control and responsibility for all 
                  credit repair activities conducted using our platform.
                </p>
              </div>
            </div>
          </div>

          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">1.</span> Acceptance of Terms
              </h2>
              <p className="text-slate-700 leading-relaxed">
                By accessing and using DisputeKing.AI ("Service", "Platform", "we", "us", or "our"), you accept 
                and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not 
                use our Service.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">2.</span> Service Description
              </h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                DisputeKing.AI provides:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>AI-powered dispute letter generation tools</li>
                <li>Credit report analysis software</li>
                <li>Document management and tracking systems</li>
                <li>CRM tools for credit repair professionals</li>
                <li>Educational resources and guidance</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-3">
                We do NOT provide credit repair services directly. We are a software technology provider that 
                gives you the tools to perform your own credit repair activities or manage your clients if you 
                are a credit repair professional.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">3.</span> User Responsibilities
              </h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                As a user of DisputeKing.AI, you are responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Providing accurate and truthful information</li>
                <li>Complying with all applicable federal, state, and local laws including the Credit Repair Organizations Act (CROA) and Fair Credit Reporting Act (FCRA)</li>
                <li>Reviewing all generated content before submission to credit bureaus or creditors</li>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>Using the Service only for lawful purposes</li>
                <li>Not making false or fraudulent credit disputes</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">4.</span> Subscription and Payment
              </h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Subscription Tiers:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li><strong>DIY Client:</strong> $59.99/month - For individual users</li>
                <li><strong>Broker Pro:</strong> $199/month - For credit repair professionals</li>
                <li><strong>Broker Enterprise:</strong> $299/month - For larger credit repair operations</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-3">
                All subscriptions automatically renew unless canceled. You may cancel at any time through your 
                account settings. Refunds are provided according to our refund policy.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">5.</span> Disclaimers and Limitations
              </h2>
              <div className="bg-slate-100 rounded-xl p-6 space-y-3">
                <p className="text-slate-700 leading-relaxed">
                  <strong>NO GUARANTEES:</strong> We do not guarantee specific credit score improvements or 
                  dispute outcomes. Credit repair results vary based on individual circumstances.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  <strong>NOT LEGAL ADVICE:</strong> Our Service does not constitute legal or financial advice. 
                  Consult with qualified professionals for specific legal or financial guidance.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  <strong>AI LIMITATIONS:</strong> While our AI tools are sophisticated, they are not perfect. 
                  Always review and customize generated content before use.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">6.</span> Prohibited Activities
              </h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                You may NOT use DisputeKing.AI to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Create false or fraudulent credit disputes</li>
                <li>Impersonate others or provide false identification</li>
                <li>Violate any federal or state laws regarding credit repair</li>
                <li>Share your account access with unauthorized users</li>
                <li>Attempt to reverse engineer or copy our software</li>
                <li>Use automated bots or scrapers on our platform</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">7.</span> Intellectual Property
              </h2>
              <p className="text-slate-700 leading-relaxed">
                All content, features, and functionality of DisputeKing.AI are owned by us and protected by 
                copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, 
                or create derivative works without our express written permission.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">8.</span> Data Privacy
              </h2>
              <p className="text-slate-700 leading-relaxed">
                We take your privacy seriously. Your personal and credit information is encrypted and protected. 
                We will never sell your data to third parties. Please review our Privacy Policy for complete details 
                on how we collect, use, and protect your information.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">9.</span> Termination
              </h2>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to suspend or terminate your account if you violate these Terms of Use or 
                engage in fraudulent activities. You may terminate your account at any time by canceling your subscription.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">10.</span> Limitation of Liability
              </h2>
              <p className="text-slate-700 leading-relaxed">
                To the maximum extent permitted by law, DisputeKing.AI shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages resulting from your use or inability to 
                use the Service.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">11.</span> Changes to Terms
              </h2>
              <p className="text-slate-700 leading-relaxed">
                We may update these Terms of Use from time to time. Continued use of the Service after changes 
                constitutes acceptance of the updated terms. We will notify users of significant changes via email 
                or platform notification.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-violet-600">12.</span> Contact Information
              </h2>
              <p className="text-slate-700 leading-relaxed">
                For questions about these Terms of Use, please contact us at:
              </p>
              <div className="bg-violet-50 rounded-xl p-4 mt-3">
                <p className="text-slate-700"><strong>Email:</strong> support@disputeking.ai</p>
                <p className="text-slate-700"><strong>Website:</strong> DisputeKing.AI</p>
              </div>
            </section>
          </div>

          {/* Footer Notice */}
          <div className="mt-12 pt-8 border-t-2 border-slate-200">
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-6">
              <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <p className="text-blue-800 text-sm">
                <strong>Your Rights:</strong> By using DisputeKing.AI, you retain all rights to your data and 
                credit information. We are here to empower you with tools and knowledge—you remain in complete 
                control of your credit repair journey.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
